import * as pdfjsLib from 'pdfjs-dist';
import React, { FC, memo, KeyboardEvent } from 'react';
import Error from './Error';
import PDFViewer from './Viewer/PDFViewer';
import classes from './Viewer.module.scss';
import { useAppSelector } from 'store/hook';
import XLSXViewer from './XLSXViewer/XLSXViewer';
import ImageViewer from './ImageViewer/ImageViewer';
import VideoViewer from './VideoViewer/VideoViewer';
import Spinner from 'components/Spinner/Spinner';
import { useTranslation } from 'react-i18next';
import { audioExtensions, convertableTypesFiles, imageExtensions, videoExtensions } from 'helpers/filesExtensions';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  '/helpers/pdf.worker.js';

type WebViewerType = {
  url?: any;
  data64?: string;
  isLoading: boolean;
  errorMessage: string;
  onClose?: () => void;
  isMobile?: boolean;
  getFile?: () => Promise<void>
};

const WebViewer: FC<WebViewerType> = ({ url, isLoading, data64, errorMessage, onClose, getFile, isMobile = false }) => {
  const { documentExtention } = useAppSelector(state => state.viewer);
  const onEscapeHandle = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      onClose && onClose();
    }
  };

  const { t } = useTranslation();
  const isStatic = onClose ? false : true;
  
  const chooseViewer = () => {
    let viewer = '';
    if (documentExtention === 'xlsx') {
      viewer = 'xlsx';
    } else if (imageExtensions.includes(documentExtention)) {
      viewer = 'image';
    } else if (videoExtensions.includes(documentExtention) || audioExtensions.includes(documentExtention)) {
      viewer = 'video';
    } else if ([...convertableTypesFiles, 'pdf'].includes(documentExtention)) {
      viewer = 'pdf';
    };

    switch(viewer) {
      case 'xlsx':
        return <XLSXViewer data={url} onClose={onClose} isStatic={isStatic}/>;
      case 'image':
        return <ImageViewer url={url} onClose={onClose} isStatic={isStatic}/>;
      case 'video':
        return <VideoViewer url={url} onClose={onClose} isStatic={isStatic}/>;
      case 'pdf':
        return <PDFViewer url={url} onClose={onClose} data64={data64} isModal={true} isMobile={isMobile} isStatic={isStatic}/>;
      default: 
        return <div className={classes.notSupported}>
          <div>{t('Documents.error.unSupportedView')}</div>
        </div>;
    };
  };
  
  return (
    <>
      { !isStatic && <div tabIndex={0} className={classes.overlay} onClick={onClose}></div> }
      <div
        onKeyDown={onEscapeHandle}
        ref={(ref) => ref?.focus()}
        tabIndex={0}
        className={isStatic ? classes.Viewer : classes.WebViewer}
      >
        {isLoading
          ? <Spinner size={60} withLogo={true}/>
          : ( errorMessage
            ? <Error msg={errorMessage} onClose={onClose} getFile={getFile}/>
            : chooseViewer()
          )
        }
      </div>
    </>
  
  );
};

export default memo(WebViewer);
